const TABLE_MIME_TYPE = "application/vnd.cs2n.table+json";
const DEFAULT_ROWS = 3;
const DEFAULT_COLUMNS = 3;
const MAX_ROWS = 50;
const MAX_COLUMNS = 20;
const DEFAULT_COLORS = {
  header: "#f4f4f4",
  row: "#ffffff",
  alternate: "#f7f7f7",
  border: "#d8d8d8",
  header_text: "#222222",
  row_text: "#222222",
  alternate_text: "#222222"
};
const COLOR_PRESETS = [
  { name: "CS2N Blue", colors: { header: "#1779ba", row: "#ffffff", alternate: "#eef6fb", border: "#b9d7ea", header_text: "#ffffff", row_text: "#222222", alternate_text: "#222222" } },
  { name: "CMU Red", colors: { header: "#c41230", row: "#ffffff", alternate: "#fdf0f3", border: "#e3b6bf", header_text: "#ffffff", row_text: "#222222", alternate_text: "#222222" } },
  { name: "CS2N Green", colors: { header: "#3adb76", row: "#ffffff", alternate: "#effaf3", border: "#b9e5c8", header_text: "#1f1f1f", row_text: "#222222", alternate_text: "#222222" } },
  { name: "Neutral", colors: { header: "#4a4a4a", row: "#ffffff", alternate: "#f4f4f4", border: "#cacaca", header_text: "#ffffff", row_text: "#222222", alternate_text: "#222222" } }
];
const tableEditorState = new WeakMap();

function newCell(value = "") {
  return {
    value,
    rowspan: 1,
    colspan: 1,
    hidden: false,
    image_attachment_id: null,
    image_url: null,
    image_filename: null,
    image_alt: ""
  };
}

function normalizeCell(cell) {
  if (typeof cell === "string") return newCell(cell);
  return {
    value: (cell && cell.value) || "",
    rowspan: Math.max(parseInt(cell && cell.rowspan, 10) || 1, 1),
    colspan: Math.max(parseInt(cell && cell.colspan, 10) || 1, 1),
    hidden: !!(cell && cell.hidden),
    image_attachment_id: cell && cell.image_attachment_id ? parseInt(cell.image_attachment_id, 10) : null,
    image_url: (cell && cell.image_url) || null,
    image_filename: (cell && cell.image_filename) || null,
    image_alt: (cell && cell.image_alt) || ""
  };
}

function normalizeData(data) {
  const rows = Math.min(Math.max(parseInt(data && data.rows, 10) || DEFAULT_ROWS, 1), MAX_ROWS);
  const columns = Math.min(Math.max(parseInt(data && data.columns, 10) || DEFAULT_COLUMNS, 1), MAX_COLUMNS);
  return {
    rows,
    columns,
    colors: Object.assign({}, DEFAULT_COLORS, (data && data.colors) || {}),
    cells: Array.from({ length: rows }, (_, r) =>
      Array.from({ length: columns }, (_, c) => normalizeCell(data && data.cells && data.cells[r] && data.cells[r][c]))
    )
  };
}

function serializableData(data) {
  const normalized = normalizeData(data);
  return {
    rows: normalized.rows,
    columns: normalized.columns,
    colors: normalized.colors,
    cells: normalized.cells.map((row) => row.map((cell) => ({
      value: cell.value,
      rowspan: cell.rowspan,
      colspan: cell.colspan,
      hidden: cell.hidden,
      image_attachment_id: cell.image_attachment_id,
      image_alt: cell.image_alt
    })))
  };
}

function csrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.content : "";
}

function attachmentData(attachment) {
  try {
    const template = document.createElement("template");
    template.innerHTML = attachment.getAttribute("content") || "";
    const wrapper = template.content.querySelector("[data-rich-text-table]");
    return {
      id: wrapper ? parseInt(wrapper.dataset.richTextTableId, 10) : null,
      data: normalizeData(wrapper ? JSON.parse(wrapper.dataset.richTextTable) : null)
    };
  } catch (_error) {
    return { id: null, data: normalizeData(null) };
  }
}

function attachmentFromPayload(payload) {
  return new Trix.Attachment({
    sgid: payload.sgid,
    contentType: payload.content_type,
    content: payload.content
  });
}

async function persistTable(id, data) {
  const url = id ? `/admin/rich_text_tables/${id}` : "/admin/rich_text_tables";
  const response = await fetch(url, {
    method: id ? "PATCH" : "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": csrfToken()
    },
    body: JSON.stringify({ rich_text_table: serializableData(data) })
  });

  if (!response.ok) throw new Error(`Unable to save table (${response.status})`);
  return response.json();
}

async function uploadTableImage(tableId, file) {
  const formData = new FormData();
  formData.append("image", file);

  const response = await fetch(`/admin/rich_text_tables/${tableId}`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: {
      "Accept": "application/json",
      "X-CSRF-Token": csrfToken()
    },
    body: formData
  });

  if (!response.ok) throw new Error(`Unable to upload image (${response.status})`);
  return response.json();
}

function createButton(label, action, className) {
  const button = document.createElement("button");
  button.type = "button";
  button.textContent = label;
  button.className = className || "button tiny secondary";
  button.dataset.tableEditorAction = action;
  return button;
}

function createColorField(label, key) {
  const field = document.createElement("label");
  field.className = "cs2n-trix-table-editor__color";
  field.appendChild(document.createTextNode(label));
  const input = document.createElement("input");
  input.type = "color";
  input.dataset.tableColor = key;
  field.appendChild(input);
  return field;
}

function createPresetButton(preset, index) {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "cs2n-trix-table-editor__preset";
  button.dataset.tablePreset = index;
  button.title = preset.name;

  const swatches = document.createElement("span");
  swatches.className = "cs2n-trix-table-editor__preset-swatches";
  [preset.colors.header, preset.colors.row, preset.colors.alternate].forEach((color) => {
    const swatch = document.createElement("span");
    swatch.style.backgroundColor = color;
    swatches.appendChild(swatch);
  });

  const label = document.createElement("span");
  label.textContent = preset.name;
  button.appendChild(swatches);
  button.appendChild(label);
  return button;
}

function selectedCoordinates(panel) {
  return Array.from(panel.querySelectorAll("input[data-table-cell-select]:checked")).map((checkbox) => ({
    row: parseInt(checkbox.dataset.row, 10),
    column: parseInt(checkbox.dataset.column, 10)
  }));
}

function mergeSelected(editorElement) {
  const panel = ensurePanel(editorElement);
  const state = tableEditorState.get(editorElement);
  const selected = selectedCoordinates(panel);
  if (!state || selected.length < 2) return;

  const rs = selected.map((c) => c.row);
  const cs = selected.map((c) => c.column);
  const minR = Math.min.apply(null, rs), maxR = Math.max.apply(null, rs);
  const minC = Math.min.apply(null, cs), maxC = Math.max.apply(null, cs);

  if (minR === 0 && maxR > 0) return window.alert("Header cells can only merge with header cells.");
  if (selected.length !== (maxR - minR + 1) * (maxC - minC + 1)) return window.alert("Select a rectangular group of cells.");

  for (let r = minR; r <= maxR; r += 1) {
    for (let c = minC; c <= maxC; c += 1) {
      const cell = state.data.cells[r][c];
      if (cell.hidden || cell.rowspan > 1 || cell.colspan > 1) return window.alert("Unmerge existing merged cells first.");
    }
  }

  const anchor = state.data.cells[minR][minC];
  anchor.rowspan = maxR - minR + 1;
  anchor.colspan = maxC - minC + 1;
  for (let r = minR; r <= maxR; r += 1) {
    for (let c = minC; c <= maxC; c += 1) {
      if (r !== minR || c !== minC) state.data.cells[r][c].hidden = true;
    }
  }
  renderPanel(editorElement);
}

function unmergeSelected(editorElement) {
  const panel = ensurePanel(editorElement);
  const state = tableEditorState.get(editorElement);
  if (!state) return;

  selectedCoordinates(panel).forEach(({ row, column }) => {
    const cell = state.data.cells[row][column];
    if (cell.hidden || (cell.rowspan === 1 && cell.colspan === 1)) return;
    for (let r = row; r < Math.min(row + cell.rowspan, state.data.rows); r += 1) {
      for (let c = column; c < Math.min(column + cell.colspan, state.data.columns); c += 1) state.data.cells[r][c].hidden = false;
    }
    cell.rowspan = 1;
    cell.colspan = 1;
  });
  renderPanel(editorElement);
}

function unmergeAll(data) {
  data.cells.forEach((row) => row.forEach((cell) => {
    cell.hidden = false;
    cell.rowspan = 1;
    cell.colspan = 1;
  }));
}

async function handleImageUpload(editorElement, fileInput) {
  const state = tableEditorState.get(editorElement);
  if (!state || !fileInput.files || !fileInput.files[0]) return;

  const row = parseInt(fileInput.dataset.row, 10);
  const column = parseInt(fileInput.dataset.column, 10);
  const file = fileInput.files[0];

  if (!file.type.startsWith("image/")) {
    window.alert("Please choose an image file.");
    return;
  }

  fileInput.disabled = true;

  try {
    if (!state.id) {
      const tablePayload = await persistTable(null, state.data);
      state.id = tablePayload.id;
    }

    const image = await uploadTableImage(state.id, file);
    const cell = state.data.cells[row][column];
    cell.image_attachment_id = image.id;
    cell.image_url = image.url;
    cell.image_filename = image.filename;
    if (!cell.image_alt) cell.image_alt = image.filename;
    renderPanel(editorElement);
  } catch (error) {
    console.error(error);
    window.alert("The image could not be uploaded. Please try again.");
    fileInput.disabled = false;
  }
}

function ensurePanel(editorElement) {
  const toolbar = editorElement.toolbarElement;
  if (!toolbar) return null;
  let panel = toolbar.querySelector(".cs2n-trix-table-editor");
  if (panel) return panel;

  panel = document.createElement("div");
  panel.className = "cs2n-trix-table-editor";
  panel.hidden = true;
  panel.innerHTML = '<div class="cs2n-trix-table-editor__heading">Table</div><div class="cs2n-trix-table-editor__helper">The first row is the header. Body rows alternate between the Cells and Alternate colors. Check cells to merge them. Each cell can also contain an image.</div>';

  const presets = document.createElement("div");
  presets.className = "cs2n-trix-table-editor__presets";
  COLOR_PRESETS.forEach((preset, index) => presets.appendChild(createPresetButton(preset, index)));
  panel.appendChild(presets);

  const colors = document.createElement("div");
  colors.className = "cs2n-trix-table-editor__colors";
  colors.appendChild(createColorField("Header", "header"));
  colors.appendChild(createColorField("Header text", "header_text"));
  colors.appendChild(createColorField("Cells", "row"));
  colors.appendChild(createColorField("Cell text", "row_text"));
  colors.appendChild(createColorField("Alternate", "alternate"));
  colors.appendChild(createColorField("Alternate text", "alternate_text"));
  colors.appendChild(createColorField("Border", "border"));
  panel.appendChild(colors);

  const grid = document.createElement("div");
  grid.className = "cs2n-trix-table-editor__grid";
  panel.appendChild(grid);

  const controls = document.createElement("div");
  controls.className = "cs2n-trix-table-editor__controls";
  [["+ Row", "add-row"], ["- Row", "remove-row"], ["+ Column", "add-column"], ["- Column", "remove-column"], ["Merge Selected", "merge"], ["Unmerge", "unmerge"]].forEach(([label, action]) => controls.appendChild(createButton(label, action)));
  panel.appendChild(controls);

  const actions = document.createElement("div");
  actions.className = "cs2n-trix-table-editor__actions";
  actions.appendChild(createButton("Cancel", "cancel"));
  actions.appendChild(createButton("Insert Table", "save", "button tiny primary cs2n-trix-table-editor__save"));
  panel.appendChild(actions);
  toolbar.appendChild(panel);

  panel.addEventListener("input", (event) => {
    const state = tableEditorState.get(editorElement);
    if (!state) return;
    if (event.target.dataset.tableColor) {
      state.data.colors[event.target.dataset.tableColor] = event.target.value;
      renderPanel(editorElement);
      return;
    }
    if (event.target.matches("input[data-row][data-column]:not([data-table-cell-select]):not([data-table-image])")) {
      const cell = state.data.cells[parseInt(event.target.dataset.row, 10)][parseInt(event.target.dataset.column, 10)];
      if (event.target.dataset.tableImageAlt) cell.image_alt = event.target.value;
      else cell.value = event.target.value;
    }
  });

  panel.addEventListener("change", (event) => {
    if (event.target.matches("input[data-table-image]")) handleImageUpload(editorElement, event.target);
  });

  panel.addEventListener("click", async (event) => {
    const imageRemove = event.target.closest("[data-table-image-remove]");
    if (imageRemove) {
      event.preventDefault();
      const state = tableEditorState.get(editorElement);
      if (!state) return;
      const cell = state.data.cells[parseInt(imageRemove.dataset.row, 10)][parseInt(imageRemove.dataset.column, 10)];
      cell.image_attachment_id = null;
      cell.image_url = null;
      cell.image_filename = null;
      cell.image_alt = "";
      renderPanel(editorElement);
      return;
    }

    const presetButton = event.target.closest("[data-table-preset]");
    if (presetButton) {
      event.preventDefault();
      const state = tableEditorState.get(editorElement);
      if (!state) return;
      const preset = COLOR_PRESETS[parseInt(presetButton.dataset.tablePreset, 10)];
      state.data.colors = Object.assign({}, state.data.colors, preset.colors);
      renderPanel(editorElement);
      return;
    }

    const button = event.target.closest("[data-table-editor-action]");
    if (!button) return;
    event.preventDefault();
    const state = tableEditorState.get(editorElement);
    if (!state) return;

    switch (button.dataset.tableEditorAction) {
      case "add-row":
        if (state.data.rows >= MAX_ROWS) return window.alert(`Tables can have at most ${MAX_ROWS} rows.`);
        state.data.cells.push(Array.from({ length: state.data.columns }, () => newCell()));
        state.data.rows += 1;
        renderPanel(editorElement);
        break;
      case "remove-row":
        if (state.data.rows > 1) {
          unmergeAll(state.data);
          state.data.cells.pop();
          state.data.rows -= 1;
          renderPanel(editorElement);
        }
        break;
      case "add-column":
        if (state.data.columns >= MAX_COLUMNS) return window.alert(`Tables can have at most ${MAX_COLUMNS} columns.`);
        state.data.cells.forEach((row) => row.push(newCell()));
        state.data.columns += 1;
        renderPanel(editorElement);
        break;
      case "remove-column":
        if (state.data.columns > 1) {
          unmergeAll(state.data);
          state.data.cells.forEach((row) => row.pop());
          state.data.columns -= 1;
          renderPanel(editorElement);
        }
        break;
      case "merge": mergeSelected(editorElement); break;
      case "unmerge": unmergeSelected(editorElement); break;
      case "cancel": closePanel(editorElement); break;
      case "save": await saveTable(editorElement); break;
    }
  });

  return panel;
}

function renderPanel(editorElement) {
  const panel = ensurePanel(editorElement);
  const state = tableEditorState.get(editorElement);
  if (!panel || !state) return;

  Object.keys(state.data.colors).forEach((key) => {
    const input = panel.querySelector(`[data-table-color="${key}"]`);
    if (input) input.value = state.data.colors[key];
  });

  const grid = panel.querySelector(".cs2n-trix-table-editor__grid");
  grid.innerHTML = "";
  grid.style.gridTemplateColumns = `repeat(${state.data.columns}, minmax(10rem, 1fr))`;
  grid.style.gridTemplateRows = `repeat(${state.data.rows}, auto)`;

  state.data.cells.forEach((row, rowIndex) => row.forEach((cell, columnIndex) => {
    if (cell.hidden) return;
    const wrapper = document.createElement("div");
    wrapper.className = "cs2n-trix-table-editor__cell";
    wrapper.style.gridRow = `${rowIndex + 1} / span ${cell.rowspan}`;
    wrapper.style.gridColumn = `${columnIndex + 1} / span ${cell.colspan}`;
    const isAlternate = rowIndex > 0 && rowIndex % 2 === 0;
    const bodyColor = isAlternate ? state.data.colors.alternate : state.data.colors.row;
    const textColor = rowIndex === 0 ? state.data.colors.header_text : (isAlternate ? state.data.colors.alternate_text : state.data.colors.row_text);
    wrapper.style.backgroundColor = rowIndex === 0 ? state.data.colors.header : bodyColor;
    wrapper.style.borderColor = state.data.colors.border;
    wrapper.style.color = textColor;

    const checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.dataset.tableCellSelect = "true";
    checkbox.dataset.row = rowIndex;
    checkbox.dataset.column = columnIndex;

    const content = document.createElement("div");
    content.className = "cs2n-trix-table-editor__cell-content";

    if (cell.image_url) {
      const preview = document.createElement("img");
      preview.src = cell.image_url;
      preview.alt = cell.image_alt || cell.image_filename || "Table cell image";
      preview.className = "cs2n-trix-table-editor__image-preview";
      content.appendChild(preview);
    }

    const input = document.createElement("input");
    input.type = "text";
    input.value = cell.value;
    input.dataset.row = rowIndex;
    input.dataset.column = columnIndex;
    input.placeholder = rowIndex === 0 ? `Header ${columnIndex + 1}` : `Row ${rowIndex + 1}, Column ${columnIndex + 1}`;
    input.style.color = textColor;
    content.appendChild(input);

    const imageControls = document.createElement("div");
    imageControls.className = "cs2n-trix-table-editor__image-controls";

    const fileLabel = document.createElement("label");
    fileLabel.className = "button tiny secondary";
    fileLabel.textContent = cell.image_attachment_id ? "Replace image" : "Add image";
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "image/*";
    fileInput.dataset.tableImage = "true";
    fileInput.dataset.row = rowIndex;
    fileInput.dataset.column = columnIndex;
    fileInput.hidden = true;
    fileLabel.appendChild(fileInput);
    imageControls.appendChild(fileLabel);

    if (cell.image_attachment_id) {
      const remove = document.createElement("button");
      remove.type = "button";
      remove.className = "button tiny secondary";
      remove.textContent = "Remove image";
      remove.dataset.tableImageRemove = "true";
      remove.dataset.row = rowIndex;
      remove.dataset.column = columnIndex;
      imageControls.appendChild(remove);

      const alt = document.createElement("input");
      alt.type = "text";
      alt.value = cell.image_alt || "";
      alt.placeholder = "Image alt text";
      alt.dataset.tableImageAlt = "true";
      alt.dataset.row = rowIndex;
      alt.dataset.column = columnIndex;
      alt.className = "cs2n-trix-table-editor__image-alt";
      imageControls.appendChild(alt);
    }

    content.appendChild(imageControls);
    wrapper.appendChild(checkbox);
    wrapper.appendChild(content);
    grid.appendChild(wrapper);
  }));

  panel.querySelector(".cs2n-trix-table-editor__save").textContent = state.attachment ? "Update Table" : "Insert Table";
}

function openPanel(editorElement, attachment) {
  const existing = attachment ? attachmentData(attachment) : { id: null, data: normalizeData(null) };
  tableEditorState.set(editorElement, { id: existing.id, attachment: attachment || null, data: existing.data });
  const panel = ensurePanel(editorElement);
  renderPanel(editorElement);
  panel.hidden = false;
}

function closePanel(editorElement) {
  const panel = ensurePanel(editorElement);
  if (panel) panel.hidden = true;
  tableEditorState.delete(editorElement);
  editorElement.focus();
}

async function saveTable(editorElement) {
  const state = tableEditorState.get(editorElement);
  if (!state) return;
  const saveButton = ensurePanel(editorElement).querySelector(".cs2n-trix-table-editor__save");
  saveButton.disabled = true;
  saveButton.textContent = "Saving...";

  try {
    const payload = await persistTable(state.id, state.data);
    state.id = payload.id;
    const fresh = attachmentFromPayload(payload);
    if (state.attachment) state.attachment.setAttributes(fresh.getAttributes());
    else editorElement.editor.insertAttachment(fresh);
    closePanel(editorElement);
  } catch (error) {
    console.error(error);
    window.alert("The table could not be saved. Please try again.");
    saveButton.disabled = false;
    saveButton.textContent = state.attachment ? "Update Table" : "Insert Table";
  }
}

function tableAttachmentForFigure(editorElement, figure) {
  const figures = Array.from(editorElement.querySelectorAll("figure.attachment"));
  const attachment = editorElement.editor.getDocument().getAttachments()[figures.indexOf(figure)];
  if (!attachment) return null;
  const content = attachment.getAttribute("content") || "";
  return attachment.getAttribute("contentType") === TABLE_MIME_TYPE || content.indexOf("data-rich-text-table-id") !== -1 ? attachment : null;
}

function addToolbarButton(editorElement) {
  const toolbar = editorElement.toolbarElement;
  if (!toolbar || toolbar.querySelector('[data-trix-action="x-table"]')) return;
  const group = toolbar.querySelector(".trix-button-group--block-tools") || toolbar.querySelector(".trix-button-row");
  if (!group) return;
  const button = document.createElement("button");
  button.type = "button";
  button.className = "trix-button trix-button--icon cs2n-trix-table-button";
  button.dataset.trixAction = "x-table";
  button.title = "Insert table";
  button.tabIndex = -1;
  button.textContent = "Table";
  group.appendChild(button);
  ensurePanel(editorElement);
}

document.addEventListener("trix-initialize", (event) => addToolbarButton(event.target));
document.addEventListener("trix-action-invoke", (event) => {
  if (event.actionName !== "x-table") return;
  event.preventDefault();
  openPanel(event.target);
});
document.addEventListener("dblclick", (event) => {
  const figure = event.target.closest("figure.attachment");
  const editorElement = event.target.closest("trix-editor");
  if (!figure || !editorElement) return;
  const attachment = tableAttachmentForFigure(editorElement, figure);
  if (!attachment) return;
  event.preventDefault();
  openPanel(editorElement, attachment);
});
